import { VStack, HStack, Text, Icon, Box, Button, Center } from "@chakra-ui/react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { SECTION_CONFIG } from "../../constants/sections"
import { useSection } from "../../hooks/useSection"
import { SectionName } from "../../state/sectionSlice"

interface FeatureListProps {
  preferencesData?: gqlTypes.GetPreferencesQuery | null
}

export const FeatureList = ({ preferencesData }: FeatureListProps) => {
  const { activeSection, navigateToSection, navigateToSectionHome } = useSection()

  const features = preferencesData?.getPreferences?.features || []
  // Type assertion needed until GraphQL types are regenerated with copy field
  const tenantCopy = (preferencesData?.getPreferences?.tenantInformation?.preferences as any)?.copy || []

  // Filter to only the 8 supported sections and enabled features
  const enabledSections = features
    .filter((feature) => {
      if (!feature) return false
      const sectionName = feature.name
      return sectionName in SECTION_CONFIG && feature.enabled
    })
    .map((feature) => {
      const sectionName = feature!.name
      const config = SECTION_CONFIG[sectionName]
      const copyOverride = tenantCopy.find((c: any) => c?.type === sectionName)

      return {
        id: sectionName,
        name: copyOverride?.name || config.name,
        icon: config.icon,
        order: config.order,
        preview: feature!.preview,
      }
    })
    .sort((a, b) => a.order - b.order)

  const handleFeatureClick = (sectionName: string) => {
    const sectionNameTyped = sectionName as SectionName

    // If clicking on the currently active section, navigate to section home
    if (activeSection === sectionNameTyped) {
      navigateToSectionHome(sectionNameTyped)
    } else {
      // Otherwise, navigate to the section (with stored sub-route if any)
      navigateToSection(sectionNameTyped)
    }
  }

  const SectionText = ({ name, fontSize = "16px" }: { name: string; fontSize?: string }) => (
    <Text
      fontSize={{ base: "10px", md: fontSize }}
      fontWeight="medium"
      flex={1}
      display={{ base: "none", md: "block" }}
    >
      {name}
    </Text>
  )

  return (
    <VStack gap={2} align="stretch" py={2}>
      {enabledSections.map((section) => (
        <Box key={section.id} width="full" px={1}>
          <Button
            variant={"light" as any}
            width="100%"
            className={activeSection === section.id ? "active" : ""}
            justifyContent="flex-start"
            onClick={() => handleFeatureClick(section.id)}
            height={"45px !important"}
            cursor={activeSection === section.id ? "pointer" : "pointer"}
            _hover={{
              opacity: activeSection === section.id ? 0.85 : 1,
            }}
            title={activeSection === section.id ? `Go to ${section.name} home` : `Open ${section.name}`}
          >
            <HStack gap={0.5} flexDirection={"column"} width="100%">
              <Center>
                <Icon as={section.icon} boxSize={5} />
              </Center>
              <SectionText name={section.name} fontSize="10px" />
            </HStack>
          </Button>
        </Box>
      ))}
    </VStack>
  )
}
